import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle2, Activity, Clock, User, Calendar, ListFilter } from 'lucide-react';
import { fetchCarTimeline, TimelineItem } from '@/services/carHistoryService';
import { formatDistanceToNow, format } from 'date-fns';
import { cs, enUS } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface CarHistoryTimelineProps {
  carId: string;
}

type FilterType = 'all' | 'status' | 'step' | 'event';

export const CarHistoryTimeline = ({ carId }: CarHistoryTimelineProps) => {
  const { t, i18n } = useTranslation();
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const dateLocale = i18n.language.startsWith('cs') ? cs : enUS;
  const timestampFormat = t('historyTimeline.timestampFormat');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await fetchCarTimeline(carId);
      setTimeline(data);
      setLoading(false);
    };
    load();
  }, [carId, i18n.language]);

  const getIcon = (type: TimelineItem['type']) => {
    switch (type) {
      case 'status':
        return <Activity className="h-4 w-4" />;
      case 'step':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'event':
        return <Clock className="h-4 w-4" />;
    }
  };

  const getIconColor = (type: TimelineItem['type']) => {
    switch (type) {
      case 'status':
        return 'text-blue-500';
      case 'step':
        return 'text-green-500';
      case 'event':
        return 'text-amber-500';
    }
  };

  const getBadgeVariant = (type: TimelineItem['type']): 'default' | 'secondary' | 'outline' => {
    switch (type) {
      case 'status':
        return 'default';
      case 'step':
        return 'secondary';
      case 'event':
        return 'outline';
    }
  };

  const getDotColor = (type: TimelineItem['type']) => {
    switch (type) {
      case 'status':
        return 'border-blue-500 bg-blue-500';
      case 'step':
        return 'border-green-500 bg-green-500';
      case 'event':
        return 'border-amber-500 bg-amber-500';
    }
  };

  const filteredTimeline = filter === 'all'
    ? timeline
    : timeline.filter(item => item.type === filter);

  const statusCount = timeline.filter(item => item.type === 'status').length;
  const stepCount = timeline.filter(item => item.type === 'step').length;
  const eventCount = timeline.filter(item => item.type === 'event').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-12">
        <Calendar className="h-12 w-12 mx-auto mb-3 opacity-20" />
        <p className="text-sm">{t('historyTimeline.empty')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Filter Tabs - Fixed at top */}
      <div className="flex-none space-y-3 pb-4">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterType)} className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-muted">
            <TabsTrigger value="all" className="text-xs">
              {t('historyTimeline.tabs.all')} ({timeline.length})
            </TabsTrigger>
            <TabsTrigger value="status" className="text-xs">
              <Activity className="h-3 w-3 mr-1" />
              {t('historyTimeline.tabs.status')} ({statusCount})
            </TabsTrigger>
            <TabsTrigger value="step" className="text-xs">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              {t('historyTimeline.tabs.step')} ({stepCount})
            </TabsTrigger>
            <TabsTrigger value="event" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {t('historyTimeline.tabs.event')} ({eventCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {filter !== 'all' && (
          <div className="text-xs text-muted-foreground text-center">
            {t('historyTimeline.showingFiltered', {
              count: filteredTimeline.length,
              filter: t(`historyTimeline.filterNames.${filter}`),
              itemLabel: t('historyTimeline.item', { count: filteredTimeline.length }),
            })}
          </div>
        )}
      </div>

      {/* Timeline - Scrollable content */}
      <div className="flex-1 overflow-y-auto -mx-6 px-6">
        {filteredTimeline.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">
            <ListFilter className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p className="text-sm">
              {t('historyTimeline.noFilteredItems', {
                filter: t(`historyTimeline.filterNames.${filter}`),
              })}
            </p>
          </div>
        ) : (
          <div className="relative pl-6 space-y-3 pb-6">
            {/* Vertical line */}
            <div className="absolute left-[7px] top-2 bottom-0 w-px bg-border" />

            {filteredTimeline.map((item) => {
              const isOrigin = item.id === 'origin';

              return (
                <div key={item.id} className="relative">
                  {/* Dot */}
                  <div
                    className={cn(
                      'absolute -left-6 mt-1.5 w-3 h-3 rounded-full border-2',
                      isOrigin
                        ? 'border-muted-foreground/20 bg-muted-foreground/10'
                        : cn('shadow-sm', getDotColor(item.type))
                    )}
                  />

                  <div
                    className={cn(
                      'rounded-lg p-3 border transition-colors',
                      isOrigin
                        ? 'bg-muted/10 border-border/20 opacity-60'
                        : 'bg-muted/30 hover:bg-muted/50 border-border/50'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div
                          className={cn(
                            'shrink-0',
                            isOrigin ? 'text-muted-foreground/40' : getIconColor(item.type)
                          )}
                        >
                          {getIcon(item.type)}
                        </div>
                        <span
                          className={cn(
                            'font-medium text-sm truncate',
                            isOrigin && 'text-muted-foreground/60'
                          )}
                        >
                          {item.title}
                        </span>
                      </div>
                      {!isOrigin && (
                        <Badge variant={getBadgeVariant(item.type)} className="text-xs shrink-0">
                          {t(`historyTimeline.badges.${item.type}`)}
                        </Badge>
                      )}
                    </div>

                    {item.description && (
                      <p className="text-xs text-muted-foreground mb-2 ml-6">{item.description}</p>
                    )}

                    <div className="flex items-center gap-3 text-xs text-muted-foreground ml-6 flex-wrap">
                      <span
                        className="flex items-center gap-1 cursor-help"
                        title={format(new Date(item.timestamp), timestampFormat, { locale: dateLocale })}
                      >
                        <Calendar className="h-3 w-3" />
                        {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true, locale: dateLocale })}
                      </span>
                      {item.user && (
                        <>
                          <span className="text-muted-foreground/30">•</span>
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {item.user}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Metadata display for events */}
                    {item.type === 'event' &&
                      item.metadata &&
                      Object.keys(item.metadata).length > 0 && (
                        <div className="mt-2 ml-6 pt-2 border-t border-border/30">
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                            {Object.entries(item.metadata).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-1">
                                <span className="text-muted-foreground capitalize">
                                  {key.replace(/_/g, ' ')}:
                                </span>
                                <span className="font-medium text-foreground/90">
                                  {String(value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
